// src/PhotoBookLayout.js  (v3)
// Keeps the old look & lazy‑loading behaviour but includes drag/resize.

import React, { useEffect, useState, useRef, useMemo, useLayoutEffect } from 'react';
import Cropper from 'react-easy-crop';
import { Rnd } from 'react-rnd';

/* -------------------------------------------------------------------------- */
/*  Module-level constants for usePrintScaler                                 */
/* -------------------------------------------------------------------------- */
const PROPS_TO_SCALE  = [
  'fontSize', 'lineHeight',
  'paddingTop','paddingRight','paddingBottom','paddingLeft',
  'marginTop','marginRight','marginBottom','marginLeft',
  'borderTopWidth','borderRightWidth',
  'borderBottomWidth','borderLeftWidth',
  'borderTopLeftRadius','borderTopRightRadius',
  'borderBottomLeftRadius','borderBottomRightRadius'
];
const pxRE = /px$/;

/* -------------------------------------------------------------------------- */
/*  Alignment-snap helpers                                                   */
/* -------------------------------------------------------------------------- */
const SNAP_PX = 8; // "magnet" distance in px

/**
 * Decide where the box should be while *dragging* or *resizing*.
 * Returns {pos, guide} where:
 *   pos   → new left/top coordinate in px
 *   guide → the guideline hit (or null)
 */
const snapPos = (pos, size, guides) => {
  for (const g of guides) {
    // left/top edge
    if (Math.abs(pos - g) <= SNAP_PX) return { pos: g, guide: g };
    // right/bottom edge
    if (Math.abs(pos + size - g) <= SNAP_PX) return { pos: g - size, guide: g };
    // centre-to-centre
    const centre = pos + size / 2;
    if (Math.abs(centre - g) <= SNAP_PX) return { pos: g - size / 2, guide: g };
  }
  return { pos, guide: null };
};

/* -------------------------------------------------------------------------- */
/*  Editable text                                                             */
/* -------------------------------------------------------------------------- */
const EditableText = ({ initialText, style }) => {
  const [isEditing, setIsEditing] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (isEditing && ref.current) {
      const sel = window.getSelection();
      const rng = document.createRange();
      rng.selectNodeContents(ref.current);
      rng.collapse(false);
      sel.removeAllRanges();
      sel.addRange(rng);
      ref.current.focus();
    }
  }, [isEditing]);

  return (
    <div
      ref={ref}
      contentEditable={isEditing}
      suppressContentEditableWarning
      onClick={() => setIsEditing(true)}
      onBlur={() => setIsEditing(false)}
      style={{ cursor: isEditing ? 'text' : 'pointer', outline: isEditing ? '1px solid #007bff' : 'none', ...style }}
    >
      {initialText}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */
const ratioNum = (str = '1:1') => {
  const [w, h] = str.split(':').map(Number);
  return h ? w / h : 1;
};

const pctToPx = (pct, total) => (parseFloat(pct) / 100) * total;

// Convert CSS calc expression (with percentages) to pixels
const calcToPx = (calcStr, total) => {
  if (typeof calcStr !== 'string') return 0; // Ensure calcStr is a string
  const expr = calcStr.replace(/^calc\((.*)\)$/, '$1');
  const numericExpr = expr.replace(/(\d+(?:\.\d+)?)%/g, (_, n) => (parseFloat(n) / 100) * total);
  try {
    // eslint-disable-next-line no-new-func
    return Function(`return ${numericExpr}`)();
  } catch {
    return 0;
  }
};

/* -------------------------------------------------------------------------- */
/*  Print scaler hook                                                         */
/* -------------------------------------------------------------------------- */
const usePrintScaler = (isPrintLayout, spreadRef) => {
  // The preview width (first non-print render) ↔ used as reference
  const previewW        = useRef(null);
  // Originals so we can restore them when leaving print mode
  const originals       = useRef(new Map());
  const PDF_CANVAS_W_PX = 3508;          // PDF target width

  useLayoutEffect(() => {
    const root = spreadRef.current;
    if (!root) return;

    // Leaving print mode → restore originals
    if (!isPrintLayout) {
      // Save preview width (only once)
      if (!previewW.current) previewW.current = root.offsetWidth;
      originals.current.forEach((map, el) => {
        Object.entries(map).forEach(([k,v]) => { el.style[k] = v; });
      });
      originals.current.clear();
      return;
    }

    // Entering print mode → apply scaling
    const refW  = previewW.current || root.offsetWidth || 1200;
    const k     = PDF_CANVAS_W_PX / refW;

    // Capture the current Map instance that this effect run will populate.
    // originals.current itself (the Map object) is stable unless reassigned,
    // but we are capturing its value at this point in time for the cleanup.
    const currentOriginalsMap = originals.current;
    // Ensure it's clean before populating if this is a new "print mode" session.
    // Note: The `!isPrintLayout` branch above already clears it.
    // If this is the very first run and isPrintLayout is true, it's a new Map().

    root.querySelectorAll('*').forEach(el => {
      const changed = {};
      PROPS_TO_SCALE.forEach(prop => {
        const inlineVal = el.style[prop];          // <-- only inline!
        if (inlineVal && pxRE.test(inlineVal)) {
          changed[prop] = inlineVal;               // backup
          const num     = parseFloat(inlineVal);
          el.style[prop] = `${(num * k).toFixed(2)}px`;
        }
      });
      if (Object.keys(changed).length) currentOriginalsMap.set(el, changed);
    });
    /* supply a cleanup that always puts things back */
    return () => {
      currentOriginalsMap.forEach((map, el) => {
        Object.entries(map).forEach(([k, v]) => { el.style[k] = v; });
      });
      currentOriginalsMap.clear();
    };
  }, [isPrintLayout, spreadRef]);
};

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */
const PhotoBookLayout = ({
  layout,
  images,
  photoObjects,
  /* per‑page text & meta */
  leftPageText,
  rightPageText,
  leftPageDisplayDate,
  leftPageDisplayCity,
  rightPageDisplayDate,
  rightPageDisplayCity,
  adjustedLeftPageFontSize,
  adjustedRightPageFontSize,
  photo_place_names = [],
  leftPageNumber,
  rightPageNumber,
  /* print tweaks */
  isPrintLayout = false,
  /* dynamic data (scattered text etc.) */
  leftPageData,
  rightPageData,
  /* crop callback */
  onPhotoCropChange,
  /* placeholder edit mode */
  isPlaceholderEditingActive = false,
  placeholderEditData = {},
  onPlaceholderUpdate = () => {},
  onPlaceholderSelect = () => {},
  isTextPlaceholderEditingActive = false,
  onTextPlaceholderUpdate = () => {},
  onTextPlaceholderSelect = () => {},
  onElementDragStart = () => {},
  onElementDragEnd = () => {}
}) => {
  const isDragEventRef = useRef(false);
  /* refs & state */
  const spreadRef = useRef(null);
  const pageRefs  = useRef([]);

  /* Auto-scale all pixel values when in print mode */
  usePrintScaler(isPrintLayout, spreadRef);

  /* trigger one extra render after the first paint so pageRefs have dimensions */
  const [, forceReflow] = useState(0);
  useLayoutEffect(() => {
    // next animation frame = after the first paint
    requestAnimationFrame(() => forceReflow(v => v + 1));   // re-render exactly once
  }, []);

  const [cropIdx,  setCropIdx]  = useState(null);
  const [cropperReady, setCropperReady] = useState(false); // NEW: hide overlay until image loaded
  const [crop,     setCrop]     = useState({ x: 0, y: 0 }); // still needed for live updates
  const [initialPct, setInitialPct] = useState(null);    // NEW: for restoring percentage crop
  const [zoom,     setZoom]     = useState(1);
  const [cropSize, setCropSize] = useState(null);
  const [cropPos,  setCropPos]  = useState(null);
  const [cropPct,  setCropPct]  = useState(null);
  const [crops,    setCrops]    = useState({});

  const [tooltip,  setTooltip]  = useState(null);  // {x,y,data}
  const [activeGuides, setActiveGuides] = useState({ v: null, h: null }); // live snapping guides

  /* avoid a React re-render on *every* drag pixel ------------------------ */
  const lastGuidesRef = useRef({ v: null, h: null });
  const updateGuides  = (v, h) => {
    if (lastGuidesRef.current.v !== v || lastGuidesRef.current.h !== h) {
      lastGuidesRef.current = { v, h };
      setActiveGuides({ v, h });
    }
  };

  /* flatten placeholders */
  const placeholders = useMemo(() => {
    const l = layout.leftPage?.photoPlaceholders  ?? [];
    const r = layout.rightPage?.photoPlaceholders ?? [];
    return [
      ...l.map((p,i) => ({ ...p,page:0,idxOnPage:i,globalIdx:i })),
      ...r.map((p,i) => ({ ...p,page:1,idxOnPage:i,globalIdx:l.length+i })),
    ];
  }, [layout]);

  /* init crop cache */
  useEffect(() => {
    if (!photoObjects?.length) return;
    const init = {};
    photoObjects.forEach((p,i) => {
      if (p?.cropInfo) {
        init[i] = { 
          url:p.url||p.baseUrl, 
          crop:p.cropInfo.crop??{x:0,y:0}, // For react-easy-crop {x,y} pixels
          zoom:p.cropInfo.zoom??1,          // For react-easy-crop zoom
          pct:p.cropInfo.croppedAreaPercentages??null // For display style (percentages)
        };
      }
    });
    setCrops(init);
  }, [photoObjects]);

  /* util: page size */
  const pageSize = (p) => {
    const ref = pageRefs.current[p];
    return ref ? { w: ref.offsetWidth, h: ref.offsetHeight } : { w:0,h:0 };
  };

  /* tooltip */
  const showTip = (e,data) => {
    if (!data) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTooltip({ x:r.left+window.scrollX, y:r.top+window.scrollY, data });
  };
  const hideTip = () => setTooltip(null);

  /* start crop */
  const beginCrop = (ph) => {
    if (isPlaceholderEditingActive) return;
    setCropIdx(ph.globalIdx);
    setCropperReady(false); // NEW: hide overlay until image is loaded
    const ovr = placeholderEditData[ph.globalIdx] || {};
    const widthPct = ovr.widthOverride ?? ph.width;
    const aspect = ovr.aspectRatioOverride ?? ph.aspectRatio;
    const ratio = ratioNum(aspect);
    const { w: pageW, h: pageH } = pageSize(ph.page);
    const { w: page0W } = pageSize(0);

    const cw = pctToPx(widthPct, pageW);
    const ch = cw / ratio;

    const baseTop = pctToPx(ph.top, pageH);
    const baseLeft = pctToPx(ph.left, pageW);
    const offsetY = ovr.offsetY ? pctToPx(ovr.offsetY, pageH) : 0;
    const offsetX = ovr.offsetX ? pctToPx(ovr.offsetX, pageW) : 0;
    const top = baseTop + offsetY;
    const left = baseLeft + offsetX + (ph.page === 1 && page0W ? page0W : 0);

    setCropSize({ width: cw, height: ch });
    setCropPos({ top, left });

    const existing = crops[ph.globalIdx] || photoObjects?.[ph.globalIdx]?.cropInfo || {};
    setInitialPct(existing.croppedAreaPercentages ?? existing.pct ?? null); // NEW: restore percentage crop
    setCrop({ x: 0, y: 0 }); // Cropper will set actual value internally
    setZoom(existing.zoom ?? 1);
    // cropPct (percentages for display) is not set here, it's derived from onCropComplete
  };
  
  const saveCrop = () => {
    if (cropIdx==null) return;
    const currentImage = photoObjects?.[cropIdx]?.url || photoObjects?.[cropIdx]?.baseUrl || images[cropIdx];
    // Save only zoom and croppedAreaPercentages (not pixel crop)
    setCrops(prev => ({
      ...prev,
      [cropIdx]: {
        url: currentImage,
        zoom,
        croppedAreaPercentages: cropPct
      }
    }));
    onPhotoCropChange?.(cropIdx, {
      zoom,
      croppedAreaPercentages: cropPct
    });
    setCropIdx(null);
  };

  const cropStyleFor = (idx) => {
    const raw = crops[idx] || photoObjects?.[idx]?.cropInfo;
    const d = raw?.croppedAreaPercentages ? { ...raw, pct: raw.croppedAreaPercentages } : raw;

    // d.pct is croppedAreaPercentages {x, y, width, height} from react-easy-crop.
    // These are percentages of the original image dimensions.
    // e.g., pct.width = 80 means 80% of the original image's width.
    if (!d?.pct || d.pct.width == null || d.pct.width <= 0 || d.pct.height == null || d.pct.height <= 0) {
      // If no crop data, or invalid crop (e.g., zero or negative width/height),
      // return an empty object. The existing styles (cover/center) might apply.
      return {};
    }

    const { pct } = d; // pct.x, pct.y, pct.width, pct.height are numbers (e.g., 0-100)

    // Calculate background-size:
    // Goal: Make the cropped portion (defined by pct.width, pct.height of original image)
    // fill the container.
    // If pct.width = 50 (crop is 50% of original image width),
    // then background image must be scaled to (100 / 50) * 100 = 200% of container width.
    const backgroundSizeWidth = `${(100 / pct.width) * 100}%`;
    const backgroundSizeHeight = `${(100 / pct.height) * 100}%`;

    // Calculate background-position percentages (posX, posY):
    // The formula P_x = pct.x / (100 - pct.width) correctly calculates the
    // CSS percentage for background-position when used with the above background-size.
    // This formula ensures that the correct part of the scaled image is shown.

    let posXPercent = 0;
    // If pct.width is 100 (full width of image is selected), pct.x must be 0.
    // In this case, posXPercent should be 0. (100 - pct.width) would be 0, causing division by zero.
    if (pct.width < 100) { // Check pct.width < 100 to ensure (100 - pct.width) is not zero
      posXPercent = (pct.x / (100 - pct.width)) * 100;
    }
    // If pct.width >= 100 (it should ideally be exactly 100 if not < 100 from react-easy-crop),
    // posXPercent remains 0. This is correct if pct.x is 0.

    let posYPercent = 0;
    if (pct.height < 100) { // Check pct.height < 100
      posYPercent = (pct.y / (100 - pct.height)) * 100;
    }
    // If pct.height >= 100, posYPercent remains 0. Correct if pct.y is 0.

    return {
      backgroundSize: `${backgroundSizeWidth} ${backgroundSizeHeight}`,
      backgroundPosition: `${posXPercent}% ${posYPercent}%`,
    };
  };

  /* render page */
  const renderPage = (pageLayout, pageDynamicData, pageNum, pageText, date, city, fontSize, pageIdx) => {
    if (!pageLayout) return null;
    const phOnPage = placeholders.filter(p=>p.page===pageIdx);

    const currentPagew = pageSize(pageIdx).w;
    const currentPageH = pageSize(pageIdx).h;

    let guideDisplayPageOffsetX = 0;
    if (pageIdx === 1) {
        if (pageRefs.current[0]) {
            guideDisplayPageOffsetX = pageRefs.current[0].offsetWidth;
        } else {
            // Fallback if page 0 ref isn't available yet (e.g., initial render)
            if (spreadRef.current) {
                guideDisplayPageOffsetX = spreadRef.current.offsetWidth / 2;
            }
        }
    }

    // Calculate guides directly without useMemo
    const boxMetrics = phOnPage.map(ph => {
      const ovr    = placeholderEditData[ph.globalIdx] || {};
      const wPct   = ovr.widthOverride ?? ph.width;
      const ratio  = ratioNum(ovr.aspectRatioOverride ?? ph.aspectRatio);
      const wPx    = pctToPx(wPct, currentPagew);
      const hPx    = wPx / ratio;
      const lPct   = ph.left; // Base left as percentage string
      const tPct   = ph.top;  // Base top as percentage string
      
      let lPx = pctToPx(lPct, currentPagew);
      let tPx = pctToPx(tPct, currentPageH);

      if (ovr.offsetX) {
        lPx += pctToPx(ovr.offsetX, currentPagew);
      }
      if (ovr.offsetY) {
        tPx += pctToPx(ovr.offsetY, currentPageH);
      }
      return { lPx, tPx, rPx: lPx + wPx, bPx: tPx + hPx };
    });

    const vElementGuides = boxMetrics.flatMap(b => [b.lPx, b.rPx, (b.lPx + b.rPx) / 2]);
    const hElementGuides = boxMetrics.flatMap(b => [b.tPx, b.bPx, (b.tPx + b.bPx) / 2]);
    const vPageGuides = [0, currentPagew / 2, currentPagew];
    const hPageGuides = [0, currentPageH / 2, currentPageH];

    const vGuides = [...new Set([...vElementGuides, ...vPageGuides])];
    const hGuides = [...new Set([...hElementGuides, ...hPageGuides])];
    // End of direct guide calculation

    const getSnapHandlers = (
      elementType, // 'placeholder' or 'textbox'
      elementId,   // ph.globalIdx for placeholder, pageIdx for textbox
      baseLpxIfPlaceholder, // Original X position in px for placeholder, 0 for textbox
      baseTpxIfPlaceholder, // Original Y position in px for placeholder, 0 for textbox
      initialWidthPx, 
      initialHeightPx,
      guideOffsetForDisplay // For displaying guides correctly on the right page
    ) => {
        const applySnap = (x, y, w, h, nodeOrRef) => {
            const sx = snapPos(x, w, vGuides);
            const sy = snapPos(y, h, hGuides);
            const targetStyle = nodeOrRef.style || (nodeOrRef.current && nodeOrRef.current.style);
            if (targetStyle) {
                 targetStyle.transform = `translate(${sx.pos}px, ${sy.pos}px)`;
            }
            updateGuides(
                sx.guide != null ? sx.guide + guideOffsetForDisplay : null,
                sy.guide
            );
            return { sx, sy };
        };

        return {
            onDragStart: () => {
                onElementDragStart();
                isDragEventRef.current = false;
            },
            onDrag: (e, d) => {
                isDragEventRef.current = true;
                applySnap(d.x, d.y, d.node.offsetWidth, d.node.offsetHeight, d.node);
            },
            onResizeStart: () => {
                onElementDragStart();
                isDragEventRef.current = false;
            },
            onResize: (e, dir, ref, delta, pos) => {
                isDragEventRef.current = true;
                applySnap(pos.x, pos.y, ref.offsetWidth, ref.offsetHeight, ref);
            },
            onDragStop: (e, d) => {
                updateGuides(null, null);
                const { sx, sy } = applySnap(d.x, d.y, initialWidthPx, initialHeightPx, { style: {} }); // Use initial dimensions for final calc

                if (elementType === 'placeholder') {
                    const newOffX = ((sx.pos - baseLpxIfPlaceholder) / currentPagew) * 100;
                    const newOffY = ((sy.pos - baseTpxIfPlaceholder) / currentPageH) * 100;
                    onPlaceholderUpdate(elementId, { offsetX: `${newOffX.toFixed(2)}%`, offsetY: `${newOffY.toFixed(2)}%` });
                    
                    if (!isDragEventRef.current) {
                        onPlaceholderSelect(elementId);
                    }
                } else if (elementType === 'textbox') {
                    const newLeft = (sx.pos / currentPagew) * 100;
                    const newTop = (sy.pos / currentPageH) * 100;
                    onTextPlaceholderUpdate(elementId, { style: { top: `${newTop.toFixed(2)}%`, left: `${newLeft.toFixed(2)}%` } });
                    
                    if (!isDragEventRef.current) {
                        onTextPlaceholderSelect(elementId);
                    }
                }
                onElementDragEnd();
            },
            onResizeStop: (e, dir, ref, delta, pos) => {
                updateGuides(null, null);
                const newWidthPx = ref.offsetWidth;
                const newHeightPx = ref.offsetHeight;
                const { sx, sy } = applySnap(pos.x, pos.y, newWidthPx, newHeightPx, { style: {} });

                if (elementType === 'placeholder') {
                    const newWpct = (newWidthPx / currentPagew) * 100;
                    const finalOffsetX = ((sx.pos - baseLpxIfPlaceholder) / currentPagew) * 100; // Recalculate offset based on snapped position
                    const finalOffsetY = ((sy.pos - baseTpxIfPlaceholder) / currentPageH) * 100;
                    onPlaceholderUpdate(elementId, {
                        widthOverride: `${newWpct.toFixed(2)}%`,
                        aspectRatioOverride: `${newWidthPx.toFixed(0)}:${newHeightPx.toFixed(0)}`,
                        offsetX: `${finalOffsetX.toFixed(2)}%`,
                        offsetY: `${finalOffsetY.toFixed(2)}%`
                    });
                    
                    if (!isDragEventRef.current) {
                        onPlaceholderSelect(elementId);
                    }
                } else if (elementType === 'textbox') {
                    const wPct = (newWidthPx / currentPagew) * 100;
                    const hPct = (newHeightPx / currentPageH) * 100;
                    const finalTop = (sy.pos / currentPageH) * 100;
                    const finalLeft = (sx.pos / currentPagew) * 100;
                    onTextPlaceholderUpdate(elementId, {
                        style: {
                            width: `${wPct.toFixed(2)}%`,
                            height: `${hPct.toFixed(2)}%`,
                            top: `${finalTop.toFixed(2)}%`,
                            left: `${finalLeft.toFixed(2)}%`
                        }
                    });
                     if (!isDragEventRef.current) {
                        onTextPlaceholderSelect(elementId);
                    }
                }
                onElementDragEnd();
            }
        };
    };

    /* base style for page‑level main text */
    let textStyle = { ...(pageLayout.text?.style||{}) };
    if (fontSize) textStyle.fontSize = `${fontSize}px`;

    return (
      <div
        ref={el=>pageRefs.current[pageIdx]=el}
        style={{ width:'50%',height:'100%',position:'relative',overflow:'hidden',background:pageLayout.background||'transparent' }}
      >
        {/* placeholders */}
        {phOnPage.map(ph=>{
          const ovr = placeholderEditData[ph.globalIdx]||{};
          const widthPct  = ovr.widthOverride??ph.width;
          const ratio     = ratioNum(ovr.aspectRatioOverride??ph.aspectRatio);
          const { w:pageW,h:pageH } = pageSize(pageIdx); // Ensure these are up-to-date if page size can change
          
          const widthPx  = pctToPx(widthPct,pageW);
          const heightPx = widthPx/ratio;
          const baseLpx  = pctToPx(ph.left,pageW);
          const baseTpx  = pctToPx(ph.top ,pageH);
          const offLpx   = ovr.offsetX?pctToPx(ovr.offsetX,pageW):0;
          const offTpx   = ovr.offsetY?pctToPx(ovr.offsetY,pageH):0;
          const rndPos   = { x:baseLpx+offLpx, y:baseTpx+offTpx };

          const imageSrc = images[ph.globalIdx] || photoObjects?.[ph.globalIdx]?.url || photoObjects?.[ph.globalIdx]?.baseUrl;
          const lazyProps = imageSrc ? { 'data-lazy-bg':'true', 'data-src':imageSrc } : {};
          const innerStyle = { width:'100%',height:'100%',backgroundColor:'#ddd',backgroundImage:'none',backgroundSize:'cover',backgroundPosition:'center',overflow:'hidden',...cropStyleFor(ph.globalIdx) };
          
          const [aspectW, aspectH] = (ovr.aspectRatioOverride ?? ph.aspectRatio)
                  .split(':')
                  .map(Number);

          if (isPlaceholderEditingActive) {
            return (
              <Rnd
                key={ph.globalIdx}
                size={{ width: widthPx, height: heightPx }}
                position={rndPos}
                bounds="parent"
                lockAspectRatio={false}
                enableResizing={{ bottomRight: true }}
                resizeHandleStyles={{
                  bottomRight: {
                    width: '14px',
                    height: '14px',
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: '2px',
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    cursor: 'se-resize',
                    zIndex: 10 // Rnd internal zIndex
                  }
                }}
                style={{
                  position:'absolute',
                  transform:`rotate(${ph.rotation||'0deg'})`,
                  cursor:'move',
                  zIndex:200, // Ensure RND is above other static elements
                  border: '1px dashed rgba(0,0,0,0.3)'
                }}
                {...getSnapHandlers(
                  'placeholder',
                  ph.globalIdx,
                  baseLpx, 
                  baseTpx,  
                  widthPx,
                  heightPx,
                  guideDisplayPageOffsetX
                )}
              >
                <div {...lazyProps} style={innerStyle} onClick={()=>beginCrop(ph)} onMouseEnter={(e)=>showTip(e,photoObjects?.[ph.globalIdx])} onMouseLeave={hideTip} />
                {pageLayout.placeName && photo_place_names[ph.globalIdx] && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: pageLayout.placeName.bottom || '-20px',
                      left: pageLayout.placeName.left || '0',
                      width: '100%',
                      textAlign: pageLayout.placeName.textAlign || 'center',
                      fontSize: pageLayout.placeName.fontSize || '10px',
                      fontFamily: pageLayout.placeName.fontFamily || 'Arial, sans-serif',
                      color: pageLayout.placeName.color || 'black',
                      ...pageLayout.placeName.style
                    }}
                  >
                    {photo_place_names[ph.globalIdx]}
                  </div>
                )}
              </Rnd>
            );
          }

          return (
            <div
              key={ph.globalIdx}
              style={{
                position: 'absolute',
                left:   `calc(${ph.left} + ${ovr.offsetX  || '0%'})`,
                top:    `calc(${ph.top}  + ${ovr.offsetY  || '0%'})`,
                width:  widthPct,
                aspectRatio: `${aspectW}/${aspectH}`,
                overflow: 'visible', // To see place names if they are outside
                transform:`rotate(${ph.rotation||'0deg'})`,
              }}
            >
              <div
                {...lazyProps}
                onClick={() => beginCrop(ph)}
                onMouseEnter={(e) => showTip(e, photoObjects?.[ph.globalIdx])}
                onMouseLeave={hideTip}
                style={{
                  width: '100%',
                  height:'100%',
                  backgroundColor:'#ddd',
                  backgroundImage:'none',
                  backgroundSize:'cover',
                  backgroundPosition:'center',
                  overflow:'hidden',
                  cursor:'pointer',
                  border: '2px solid white',
                  ...cropStyleFor(ph.globalIdx),
                }}
              />
              {pageLayout.placeName && photo_place_names[ph.globalIdx] && (
                <div
                  style={{
                    position:   'absolute',
                    bottom:     pageLayout.placeName.bottom     || '-20px',
                    left:       pageLayout.placeName.left       ||  '0',
                    width:      '100%',
                    textAlign:  pageLayout.placeName.textAlign  || 'center',
                    fontSize:   pageLayout.placeName.fontSize   || '10px',
                    fontFamily: pageLayout.placeName.fontFamily || 'Arial, sans-serif',
                    color:      pageLayout.placeName.color      || 'black',
                    ...pageLayout.placeName.style,
                  }}
                >
                  {photo_place_names[ph.globalIdx]}
                </div>
              )}
            </div>
          );
        })}

        {/* scattered text (dynamic) */}
        {pageDynamicData?.textElements?.map((t,i)=>(
          <EditableText key={`scatter-${pageIdx}-${i}`} initialText={t.content} style={{ position:'absolute', width:t.style.width, height:t.style.height, transform:`rotate(${t.style.rotation||'0deg'})`, ...t.style }} />
        ))}

        {/* main page text (now optionally draggable/resizable) */}
        {pageLayout.text && pageText && (
          isTextPlaceholderEditingActive && pageLayout.text.style // Ensure style object exists
            ? (
              <Rnd
                bounds="parent"
                lockAspectRatio={false}
                enableResizing={{
                  top: true, right: true, bottom: true, left: true,
                  topRight: true, bottomRight: true, bottomLeft: true, topLeft: true
                }}
                size={{
                  width: pctToPx(pageLayout.text.style.width, currentPagew),
                  height: pctToPx(pageLayout.text.style.height, currentPageH)
                }}
                position={{
                  x: typeof pageLayout.text.style.left === 'string'
                    ? (pageLayout.text.style.left.includes('calc')
                        ? calcToPx(pageLayout.text.style.left, currentPagew)
                        : pctToPx(pageLayout.text.style.left, currentPagew))
                    : (typeof pageLayout.text.style.left === 'number' ? pageLayout.text.style.left : 0), // Handle number case or default
                  y: typeof pageLayout.text.style.top === 'string'
                    ? (pageLayout.text.style.top.includes('calc')
                        ? calcToPx(pageLayout.text.style.top, currentPageH)
                        : pctToPx(pageLayout.text.style.top, currentPageH))
                    : (typeof pageLayout.text.style.top === 'number' ? pageLayout.text.style.top : 0) // Handle number case or default
                }}
                style={{ border: '1px dashed #007bff', zIndex: 100 }}
                resizeHandleStyles={{
                  top: { cursor: 'ns-resize' }, bottom: { cursor: 'ns-resize' },
                  left: { cursor: 'ew-resize' }, right: { cursor: 'ew-resize' },
                  topLeft: { cursor: 'nwse-resize' }, topRight: { cursor: 'nesw-resize' },
                  bottomLeft: { cursor: 'nesw-resize' }, bottomRight: { cursor: 'nwse-resize' }
                }}
                {...getSnapHandlers(
                  'textbox',
                  pageIdx,
                  0, 0, // baseLpx/Tpx not applicable for textbox relative to its own position
                  pctToPx(pageLayout.text.style.width, currentPagew),
                  pctToPx(pageLayout.text.style.height, currentPageH),
                  guideDisplayPageOffsetX
                )}
              >
                <EditableText
                  initialText={pageText}
                  style={{
                    ...textStyle, // Includes original styles and font size
                    position: 'absolute', // Positioned within Rnd
                    top: 0, left: 0, // Relative to Rnd
                    width: '100%', height: '100%', // Fills Rnd
                    overflow: 'auto' // Or 'hidden' or as per layout
                  }}
                />
              </Rnd>
            )
            : (
              <EditableText
                initialText={pageText}
                style={{
                  position:'absolute',
                  ...textStyle // This already contains top, left, width, height from pageLayout.text.style
                }}
              />
            )
        )}

        {/* date | city */}
        {pageLayout.dateCityLocation && (date||city) && (
          <div style={{ position:'absolute', ...pageLayout.dateCityLocation.style }}>{date}{date&&city?' | ':''}{city}</div>
        )}

        {/* page number */}
        {pageLayout.pageNumberLocation && pageNum != null && (
          <div
            style={{
              position: 'absolute',
              bottom: pageLayout.pageNumberLocation.style?.bottom ?? '2%',
              [ pageIdx === 0 ? 'left' : 'right' ]:
                pageLayout.pageNumberLocation.style?.[ pageIdx===0 ? 'left' : 'right' ] ?? '2%',
              fontSize: pageLayout.pageNumberLocation.style?.fontSize
                ?? (isPrintLayout ? '12px' : undefined),
              ...pageLayout.pageNumberLocation.style,
            }}
          >
            {pageNum}
          </div>
        )}
      </div>
    );
  };

  /* background style */
  const backgroundStyle = (()=>{
    const bg = layout.background;
    if (!bg) return {};
    if (bg.startsWith('#') || ['red','blue','green','yellow','black','white','purple','orange','pink','cornsilk'].includes(bg.toLowerCase())) return { backgroundColor:bg };
    return { backgroundImage:`url(${bg})`,backgroundSize:'cover',backgroundPosition:'center',backgroundRepeat:'no-repeat' };
  })();

  return (
    <div ref={spreadRef} style={{ width:'100%',margin:'0 auto',aspectRatio:'1.414/1',position:'relative',display:'flex',...backgroundStyle }}>
      <div style={{ position:'absolute',top:0,bottom:0,left:'50%',borderLeft:'2px solid #ccc',zIndex:5 }} />

      {renderPage(layout.leftPage,  leftPageData,  leftPageNumber,  leftPageText,  leftPageDisplayDate,  leftPageDisplayCity,  adjustedLeftPageFontSize, 0)}
      {renderPage(layout.rightPage, rightPageData, rightPageNumber, rightPageText, rightPageDisplayDate, rightPageDisplayCity, adjustedRightPageFontSize, 1)}

      {tooltip && (
        <div style={{ position:'absolute',left:tooltip.x,top:tooltip.y-10,background:'rgba(0,0,0,0.8)',color:'#fff',padding:'8px 12px',borderRadius:'4px',pointerEvents:'none',whiteSpace:'nowrap',zIndex:1000 }}>
          {tooltip.data.url && (
            <div style={{ fontWeight:'bold',marginBottom:'4px' }}>
              {typeof tooltip.data.url === 'string' ? tooltip.data.url.replace(/^.*[\\/]/, '') : 'Image'}
            </div>
          )}
          {/* tooltip.data.crop is {x,y} pixels for react-easy-crop, not percentages */}
          {(tooltip.data.crop || (crops[cropIdx] && crops[cropIdx].crop)) && (
            <div style={{ fontSize:'12px' }}>
              Crop (px): {Math.round((tooltip.data.crop || crops[cropIdx]?.crop)?.x ?? 0)}, {Math.round((tooltip.data.crop || crops[cropIdx]?.crop)?.y ?? 0)}<br />
              Zoom: {Math.round(((tooltip.data.zoom || crops[cropIdx]?.zoom) ?? 1) * 100)}%
            </div>
          )}
           {/* Displaying d.pct (percentages) from crops state for clarity if needed */}
           {crops[cropIdx]?.pct && (
             <div style={{ fontSize:'12px', marginTop:'4px', borderTop: '1px solid #555', paddingTop:'4px' }}>
               Crop Area (%):<br/>
               X: {crops[cropIdx].pct.x.toFixed(1)}%, Y: {crops[cropIdx].pct.y.toFixed(1)}%<br/>
               W: {crops[cropIdx].pct.width.toFixed(1)}%, H: {crops[cropIdx].pct.height.toFixed(1)}%
             </div>
           )}
        </div>
      )}

      {cropIdx!=null && cropSize && cropPos && (images[cropIdx] || photoObjects?.[cropIdx]?.url || photoObjects?.[cropIdx]?.baseUrl) && (
        <div style={{ position:'absolute',...cropPos,...cropSize,zIndex:900,border:'2px solid #000', background:'white', visibility: cropperReady ? 'visible' : 'hidden' }}>
          <Cropper
            image={photoObjects?.[cropIdx]?.url || photoObjects?.[cropIdx]?.baseUrl || images[cropIdx]}
            crop={crop}
            zoom={zoom}
            aspect={cropSize.width / cropSize.height}
            initialCroppedAreaPercentages={initialPct}
            objectFit="cover"
            cropSize={cropSize}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(croppedArea, croppedAreaPixels) => setCropPct(croppedArea)}
            onMediaLoaded={() => setCropperReady(true)} // NEW: show overlay when image loaded
          />
          <div style={{ position:'absolute',bottom:10,left:'50%',transform:'translateX(-50%)',display:'flex',gap:10, zIndex: 901 }}>
            <button onClick={saveCrop} style={{padding:'5px 10px', cursor:'pointer'}}>Confirm</button>
            <button onClick={()=>{setCropIdx(null); setCropPos(null); setCropSize(null);}} style={{padding:'5px 10px', cursor:'pointer'}}>Cancel</button>
          </div>
        </div>
      )}

      {activeGuides.v !== null && (
        <div style={{
              position:'absolute',
              left:activeGuides.v, top:0, bottom:0,
              borderLeft:'1px dotted #007bff',
              pointerEvents:'none', zIndex:950 }} />
      )}
      {activeGuides.h !== null && (
        <div style={{
              position:'absolute',
              top:activeGuides.h, left:0, right:0,
              borderTop:'1px dotted #007bff',
              pointerEvents:'none', zIndex:950 }} />
      )}
    </div>
  );
};

export default PhotoBookLayout;